import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface WorkOrder {
  id: string;
  order_id: string;
  order_number: string;
  sidemark: string;
  item_number: number;
  unit_count: number;
  width: number | string;
  height: number | string;
  frame_color: string;
  color: string;
  status: string;
  parts_bag_verified: boolean;
  dealer_company: string;
  started_at: string | null;
  completed_at: string | null;
}

export default function ShippingQueuePage() {
  const { workstationId } = useParams<{ workstationId: string }>();
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stationName, setStationName] = useState('');
  const [startingId, setStartingId] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    if (!workstationId) return;
    try {
      const data = await api.getShippingWorkstationQueue(workstationId);
      // The API may return { workstation, workOrders } or just an array
      if (Array.isArray(data)) {
        setWorkOrders(data);
      } else if (data && typeof data === 'object') {
        const obj = data as any;
        setWorkOrders(obj.workOrders || obj.work_orders || []);
        if (obj.workstation?.name || obj.name) {
          setStationName(obj.workstation?.name || obj.name);
        }
      }
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [workstationId]);

  useEffect(() => {
    loadQueue();
    const interval = setInterval(loadQueue, 10000);
    return () => clearInterval(interval);
  }, [loadQueue]);

  const handleStart = async (workOrderId: string) => {
    try {
      setStartingId(workOrderId);
      await api.startShippingWorkOrder(workOrderId);
      navigate(`/stations/${workstationId}/card/${workOrderId}`);
    } catch (err: any) {
      alert(err.message || 'Failed to start work order');
      setStartingId(null);
    }
  };

  const getStatusBadge = (wo: WorkOrder) => {
    const status = wo.status?.toLowerCase();
    if (status === 'completed' || status === 'complete') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-900/50 text-green-300 border border-green-700">Completed</span>;
    }
    if (status === 'in_progress' || status === 'in-progress' || status === 'started') {
      return <span className="px-2 py-1 text-xs rounded-full bg-yellow-900/50 text-yellow-300 border border-yellow-700">In Progress</span>;
    }
    if (status === 'skipped') {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-700 text-gray-300 border border-gray-600">Skipped</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-blue-900/50 text-blue-300 border border-blue-700">Ready</span>;
  };

  const getPartsBagBadge = (wo: WorkOrder) => {
    if (wo.parts_bag_verified) {
      return (
        <span className="inline-flex items-center gap-1 text-green-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-xs">Verified</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-red-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        <span className="text-xs">Not Verified</span>
      </span>
    );
  };

  const isInProgress = (wo: WorkOrder) => {
    const s = wo.status?.toLowerCase();
    return s === 'in_progress' || s === 'in-progress' || s === 'started';
  };

  const isReady = (wo: WorkOrder) => {
    const s = wo.status?.toLowerCase();
    return s === 'ready' || s === 'pending' || s === 'queued';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/stations')}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {stationName || 'Station Queue'}
            </h1>
            <p className="text-gray-400 mt-1">
              {workOrders.length} work order{workOrders.length !== 1 ? 's' : ''} in queue
            </p>
          </div>
        </div>
        <button
          onClick={loadQueue}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300">
          {error}
        </div>
      )}

      {workOrders.length === 0 && !error ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">Queue Empty</h3>
          <p className="text-gray-400">No work orders are waiting at this station.</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Sidemark</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Unit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Dimensions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Color</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Parts Bag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {workOrders.map((wo) => (
                  <tr key={wo.id} className="hover:bg-gray-750">
                    <td className="px-4 py-4">
                      <span className="text-white font-medium">{wo.order_number}</span>
                      {wo.dealer_company && (
                        <div className="text-gray-400 text-sm">{wo.dealer_company}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-gray-300">
                      {wo.sidemark || '-'}
                    </td>
                    <td className="px-4 py-4 text-gray-300">
                      {wo.item_number}{wo.unit_count ? ` / ${wo.unit_count}` : ''}
                    </td>
                    <td className="px-4 py-4 text-gray-300">
                      {wo.width && wo.height ? `${wo.width}" x ${wo.height}"` : '-'}
                    </td>
                    <td className="px-4 py-4 text-gray-300">
                      {wo.frame_color || wo.color || '-'}
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(wo)}
                    </td>
                    <td className="px-4 py-4">
                      {getPartsBagBadge(wo)}
                    </td>
                    <td className="px-4 py-4">
                      {isInProgress(wo) ? (
                        <button
                          onClick={() => navigate(`/stations/${workstationId}/card/${wo.id}`)}
                          className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-500 transition-colors font-medium"
                        >
                          Open Card
                        </button>
                      ) : isReady(wo) ? (
                        <button
                          onClick={() => handleStart(wo.id)}
                          disabled={startingId === wo.id}
                          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-500 disabled:opacity-50 transition-colors font-medium"
                        >
                          {startingId === wo.id ? (
                            <span className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              Starting...
                            </span>
                          ) : (
                            'Start'
                          )}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
